import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  Grid,
  Skeleton,
  Fade,
} from "@mui/material";
import {
  CardGiftcard as GiftIcon,
  Info as InfoIcon,
  SolarPower as SolarIcon,
} from "@mui/icons-material";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../../utils/swalConfig";

const DailyReward = () => {
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const [rewardStatus, setRewardStatus] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const fetchRewardStatus = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.get("/rewards/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRewardStatus(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch reward status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewardStatus();
  }, []);

  useEffect(() => {
    if (rewardStatus?.nextClaimAt && !rewardStatus.canClaim) {
      const timer = setInterval(() => {
        const now = new Date().getTime();
        const nextClaim = new Date(rewardStatus.nextClaimAt).getTime();
        const distance = nextClaim - now;

        if (distance < 0) {
          setTimeLeft(null);
          fetchRewardStatus();
          clearInterval(timer);
        } else {
          const hours = Math.floor(distance / (1000 * 60 * 60));
          const minutes = Math.floor(
            (distance % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);
          setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [rewardStatus]);

  const handleClaim = async () => {
    try {
      setClaiming(true);
      const token = localStorage.getItem("jwt");
      const response = await api.post(
        "/rewards/claim",
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setRewardStatus({
        ...rewardStatus,
        canClaim: false,
        nextClaimAt: response.data.data.nextClaimAt,
      });

      successAlert(
        "Reward Claimed!",
        `Successfully claimed ₦${response.data.data.availableBalanceAmount} to Available Balance and ₦${response.data.data.bonusWalletAmount} to Bonus Wallet`
      );

      // Trigger parent component to refresh balances
      window.location.reload();
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to claim reward"
      );
    } finally {
      setClaiming(false);
    }
  };

  // Skeleton loader component for a smoother loading experience
  const RewardSkeleton = () => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" mb={2}>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ mr: 1 }}
            />
            <Skeleton width={180} height={32} />
          </Box>

          {/* Alert */}
          <Skeleton
            variant="rectangular"
            height={60}
            sx={{ mb: 2, borderRadius: 1 }}
          />

          {/* Reward Info */}
          <Box sx={{ bgcolor: "background.paper", p: 2, borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="40%" height={32} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="40%" height={32} />
              </Grid>
            </Grid>

            <Skeleton variant="rectangular" height={1} sx={{ my: 2 }} />

            <Skeleton width="40%" height={20} sx={{ mb: 1 }} />
            <Box sx={{ pl: 2 }}>
              <Skeleton width="70%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton width="70%" height={20} />
            </Box>
          </Box>

          {/* Button or Timer */}
          <Skeleton
            variant="rectangular"
            height={48}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return <RewardSkeleton />;
  }

  return (
    <Grid item xs={12}>
      <Fade in={!loading} timeout={500}>
        <Card>
          <CardContent>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={2}>
              <SolarIcon sx={{ color: "success.main", mr: 1 }} />
              <Typography variant="h6">Investment Reward</Typography>
              <Tooltip title="This reward represents your share of profits from our solar and renewable energy investments. commission earned from the collective investment pool.">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Alert */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                This daily reward comes from profits generated through our solar
                and renewable energy investments.
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Reward Info */}
            <Box sx={{ bgcolor: "background.paper", p: 2, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Today's Investment Return:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    ₦{rewardStatus?.rewardAmount || 100}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Rewards Claimed:
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    ₦{rewardStatus?.totalClaimed || 0}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Distribution */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Distribution:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  • ₦
                  {(rewardStatus?.rewardAmount *
                    (rewardStatus?.availableBalancePercentage || 50)) /
                    100}{" "}
                  to Available Balance
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  • ₦
                  {(rewardStatus?.rewardAmount *
                    (100 - (rewardStatus?.availableBalancePercentage || 50))) /
                    100}{" "}
                  to Bonus Wallet
                </Typography>
              </Box>
            </Box>

            {/* Claim Button or Timer */}
            {rewardStatus?.canClaim ? (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleClaim}
                disabled={claiming}
                startIcon={claiming ? null : <GiftIcon />}
                sx={{
                  mt: 2,
                  position: "relative",
                  height: 48,
                }}
              >
                {claiming ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Claim Investment Return"
                )}
              </Button>
            ) : (
              <Fade in={true}>
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 2,
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Next return available in:
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {timeLeft}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Claims reset every 24 hours from your last claim)
                  </Typography>
                </Box>
              </Fade>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );
};

export default DailyReward;
